import { useState, useMemo, useEffect, useCallback } from "react";
import "./ProjectsGallery.css";

// ---------------------------------------------------------------------------
// Конфиг
// ---------------------------------------------------------------------------
const API_URL = process.env.REACT_APP_API_URL || "/api/v1";

// Категории для фильтров — берутся динамически из данных API,
// список ниже нужен только для сохранения порядка отображения кнопок.
const CATEGORY_ORDER = [
  "Все",
  "Серверные / ЦОД",
  "СКС и сети",
  "Электрика",
  "AV-системы",
  "Автоматизация",
  "Стройка / Команда",
  "Промышленность",
];

// ---------------------------------------------------------------------------
// Утилита: динамическое распределение размеров плиток
// ---------------------------------------------------------------------------
function assignSizes(items) {
  const n = items.length;
  if (n === 0) return [];

  if (n === 1) return [{ ...items[0], _size: "hero" }];
  if (n === 2) return items.map((p) => ({ ...p, _size: "wide" }));
  if (n === 3) return [
    { ...items[0], _size: "hero" },
    { ...items[1], _size: "tall" },
    { ...items[2], _size: "sm" },
  ];
  if (n === 4) return [
    { ...items[0], _size: "hero" },
    { ...items[1], _size: "sm" },
    { ...items[2], _size: "sm" },
    { ...items[3], _size: "wide" },
  ];

  const heroCount = n >= 20 ? 2 : 1;
  const tallCount = Math.max(0, Math.floor(n / 6) - heroCount);
  const wideCount = Math.max(0, Math.floor(n / 8));

  const result = [];
  let heroes = heroCount;
  let talls  = tallCount;
  let wides  = wideCount;

  for (let i = 0; i < n; i++) {
    let size = "sm";
    if (heroes > 0 && i % Math.ceil(n / heroCount) === 0) {
      size = "hero"; heroes--;
    } else if (talls > 0 && i % Math.ceil(n / (tallCount + 1)) === 1) {
      size = "tall"; talls--;
    } else if (wides > 0 && i % Math.ceil(n / (wideCount + 1)) === 2) {
      size = "wide"; wides--;
    }
    result.push({ ...items[i], _size: size });
  }
  return result;
}

// ---------------------------------------------------------------------------
// Хук: загрузка изображений галереи с бэкенда (?section=gallery)
// ---------------------------------------------------------------------------
function useGalleryImages() {
  const [images, setImages]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/images/?section=gallery`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setImages(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  return { images, loading, error, refetch: fetchImages };
}

// ---------------------------------------------------------------------------
// Компонент: одиночное изображение
// ---------------------------------------------------------------------------
function ProjectImage({ imageId, alt }) {
  const [hasError, setHasError] = useState(false);
  const [loaded,   setLoaded]   = useState(false);

  if (hasError) {
    return (
      <div className="project-image project-image--error">
        <span>{alt}</span>
      </div>
    );
  }

  return (
    <div className="project-image">
      {!loaded && <div className="project-image__skeleton" />}
      <img
        src={`${API_URL}/images/${imageId}/file`}
        alt={alt}
        className={`project-image__img${loaded ? " project-image__img--loaded" : ""}`}
        onLoad={() => setLoaded(true)}
        onError={() => setHasError(true)}
        loading="lazy"
      />
      <div className="project-image__vignette" />
      <div className="project-image__overlay" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Компонент: скелетон загрузки
// ---------------------------------------------------------------------------
function GallerySkeleton({ count = 12 }) {
  return (
    <div className="gallery-grid">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className={`gallery-item ${i === 0 ? "gallery-item--hero" : i % 5 === 1 ? "gallery-item--tall" : "gallery-item--sm"}`}
        >
          <div className="gallery-item__skeleton" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Основной компонент
// ---------------------------------------------------------------------------
export default function ProjectsGallery() {
  const [activeCategory, setActiveCategory] = useState("Все");
  const { images, loading, error, refetch }  = useGalleryImages();

  // Подсчёт по категориям (используем поле category из API)
  const counts = useMemo(() => {
    const map = new Map();
    map.set("Все", images.length);
    for (const img of images) {
      const cat = img.category || "Без категории";
      map.set(cat, (map.get(cat) ?? 0) + 1);
    }
    return map;
  }, [images]);

  // Все категории из данных, отсортированные согласно CATEGORY_ORDER
  const categories = useMemo(() => {
    const fromData = Array.from(counts.keys()).filter((c) => c !== "Все");
    const ordered  = CATEGORY_ORDER.filter((c) => fromData.includes(c));
    const rest     = fromData.filter((c) => !CATEGORY_ORDER.includes(c));
    return ["Все", ...ordered, ...rest];
  }, [counts]);

  // Фильтрация
  const filtered = useMemo(() => {
    if (activeCategory === "Все") return images;
    return images.filter((img) => (img.category || "Без категории") === activeCategory);
  }, [images, activeCategory]);

  // Динамическое распределение размеров плиток
  const visible = useMemo(() => assignSizes(filtered), [filtered]);

  const SIZE_CLASS = {
    sm:   "gallery-item--sm",
    wide: "gallery-item--wide",
    tall: "gallery-item--tall",
    hero: "gallery-item--hero",
  };

  return (
    <section id="gallery" className="gallery-section">
      <div className="gallery-container">

        {/* ── Заголовок ── */}
        <div className="gallery-header">
          <div className="gallery-header__left">
            <div className="gallery-eyebrow">Наши работы</div>
            <h2 className="gallery-title">Галерея проектов</h2>
          </div>
          <p className="gallery-description">
            Реальные фотографии с объектов — СКС, серверные, телекоммуникационные
            шкафы, электрика, СОТ, автоматика и пусконаладка инженерной
            IT-инфраструктуры
          </p>
        </div>

        {/* ── Фильтры ── */}
        {!loading && !error && (
          <div className="gallery-filters">
            {categories.map((cat) => {
              const count = counts.get(cat) ?? 0;
              if (count === 0) return null;
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`gallery-filter-btn${isActive ? " gallery-filter-btn--active" : ""}`}
                >
                  <span>{cat}</span>
                  <span className={`gallery-filter-count${isActive ? " gallery-filter-count--active" : ""}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* ── Состояния ── */}
        {loading && <GallerySkeleton count={12} />}

        {error && (
          <div className="gallery-error">
            <p>Не удалось загрузить изображения: {error}</p>
            <button onClick={refetch} className="gallery-filter-btn">
              Повторить
            </button>
          </div>
        )}

        {/* ── Сетка проектов ── */}
        {!loading && !error && (
          <>
            {visible.length === 0 ? (
              <div className="gallery-empty">
                <p>В этой категории пока нет проектов</p>
              </div>
            ) : (
              <div
                className="gallery-grid"
                style={{ "--item-count": visible.length }}
              >
                {visible.map((img, idx) => (
                  <div
                    key={`${img.id}-${idx}`}
                    className={`gallery-item ${SIZE_CLASS[img._size]}`}
                  >
                    <ProjectImage
                      imageId={img.id}
                      alt={img.title || img.filename}
                    />
                    <div className="gallery-item__gradient" />
                    <div className="gallery-item__caption">
                      {img.category && (
                        <span className="gallery-item__category">{img.category}</span>
                      )}
                      <div className="gallery-item__title">
                        {img.title || img.filename}
                      </div>
                      {img.description && (
                        <div className="gallery-item__desc">{img.description}</div>
                      )}
                    </div>
                    <div className="gallery-item__index">
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Подвал ── */}
            <div className="gallery-footer">
              <span>{visible.length} из {images.length} объектов</span>
              <span>Фото © ITS</span>
            </div>
          </>
        )}

      </div>
    </section>
  );
}